// Wave 3B — Commit 3c
// Real iOS geolocation permission flow + preference persistence for the
// "Shop nearby stores" toggle.
//
// Decisions:
//  - D2  Visual-only toggle-off. Coords stay; we just flip
//        offline_shopping_enabled = false (client-side profile update).
//  - D3  Silent refetch on window focus when the stored coords are stale
//        (>24h), the OS permission is 'granted', AND the user's saved
//        preference is enabled.
//  - D4  Soft denial UX. When location was denied at the OS level, the
//        toggle stays visually OFF and shows a muted "Enable in Settings"
//        caption plus an "Open Settings" deep link.
//  - D5  Toggle is ON only when BOTH offline_shopping_enabled = true AND
//        isFresh = true.
//  - A2  After every successful write we invalidate
//        queryKey ["offline-shopping-location", userId] so the Home UI
//        reflects the change immediately (no 60s TTL wait).

import { useCallback, useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { App as CapApp } from "@capacitor/app";
import { supabase } from "@/integrations/supabase/client";
import {
  offlineShoppingLocationKey,
  useOfflineShoppingLocation,
} from "@/hooks/useOfflineShoppingLocation";
import { getCurrentCoords } from "@/lib/nativeLocation";

interface OfflineShoppingToggleProps {
  userId: string | null | undefined;
  guestSessionId?: string | null;
}

const INK = "#2C2820";
const MUTED = "#6B6560";
const BORDER = "#F1EFE8";
const CORAL = "#C84B2F";

async function checkLocationPermission(): Promise<
  "granted" | "denied" | "prompt" | "unknown"
> {
  if (!Capacitor.isNativePlatform()) {
    // Web: navigator.permissions where available.
    try {
      // deno-lint-ignore no-explicit-any
      const p: any = (navigator as any).permissions;
      if (p?.query) {
        const status = await p.query({ name: "geolocation" });
        if (status.state === "granted") return "granted";
        if (status.state === "denied") return "denied";
        return "prompt";
      }
    } catch {
      // fall through
    }
    return "unknown";
  }
  try {
    const s = await Geolocation.checkPermissions();
    if (s.location === "granted") return "granted";
    if (s.location === "denied") return "denied";
    return "prompt";
  } catch {
    return "unknown";
  }
}

async function openAppSettings() {
  // Capacitor 8: use the App plugin's openSettings if available; otherwise
  // fall back to the app:// URL. On web we just show a hint toast.
  if (!Capacitor.isNativePlatform()) {
    toast.info("Enable location in your browser's site settings.");
    return;
  }
  try {
    // deno-lint-ignore no-explicit-any
    const anyApp = CapApp as unknown as { openSettings?: () => Promise<void> };
    if (typeof anyApp.openSettings === "function") {
      await anyApp.openSettings();
      return;
    }
  } catch (err) {
    console.warn("[OfflineShoppingToggle] openSettings failed", err);
  }
  toast.info("Open Settings → Like2Fit → Location to enable.");
}

export function OfflineShoppingToggle({ userId, guestSessionId = null }: OfflineShoppingToggleProps) {
  const qc = useQueryClient();
  const identity = { userId: userId ?? null, guestSessionId: guestSessionId ?? null };
  const hasIdentity = !!(identity.userId || identity.guestSessionId);
  const { isFresh, enabled, updatedAt, loading } =
    useOfflineShoppingLocation(identity);
  const [permission, setPermission] = useState<
    "granted" | "denied" | "prompt" | "unknown"
  >("unknown");
  const [busy, setBusy] = useState(false);

  const isOn = isFresh && enabled;

  // Poll permission on mount + when the tab regains focus.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const p = await checkLocationPermission();
      if (!cancelled) setPermission(p);
    };
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: offlineShoppingLocationKey(identity) });
  }, [qc, identity]);

  const writeCoords = useCallback(
    async (lat: number, lng: number) => {
      const body: Record<string, unknown> = { lat, lng };
      if (!identity.userId && identity.guestSessionId) {
        body.guest_session_id = identity.guestSessionId;
      }
      const { error } = await supabase.functions.invoke(
        "update-offline-location",
        { body },
      );
      if (error) {
        console.error("[OfflineShoppingToggle] update-offline-location", error);
        toast.error("Couldn't save your location. Try again.");
        return false;
      }
      invalidate();
      return true;
    },
    [invalidate, identity.userId, identity.guestSessionId],
  );

  const setEnabled = useCallback(
    async (value: boolean) => {
      if (!hasIdentity) return false;
      if (identity.userId) {
        const { error } = await supabase
          .from("profiles")
          .update({ offline_shopping_enabled: value })
          .eq("id", identity.userId);
        if (error) {
          console.error("[OfflineShoppingToggle] setEnabled", error);
          toast.error("Couldn't update your preference.");
          return false;
        }
      } else if (identity.guestSessionId) {
        const { error } = await (supabase as unknown as {
          rpc: (fn: string, args: Record<string, unknown>) => Promise<{ error: unknown }>;
        }).rpc("set_guest_offline_location", {
          p_guest_session_id: identity.guestSessionId,
          // Preserve existing coords by passing them back; if unknown, null.
          p_lat: null,
          p_lng: null,
          p_enabled: value,
        });
        if (error) {
          console.error("[OfflineShoppingToggle] setEnabled(guest)", error);
          toast.error("Couldn't update your preference.");
          return false;
        }
      }
      invalidate();
      return true;
    },
    [invalidate, hasIdentity, identity.userId, identity.guestSessionId],
  );

  // D3 — Silent refetch on focus when stale + granted + enabled.
  useEffect(() => {
    if (!hasIdentity) return;
    const onFocus = async () => {
      if (permission !== "granted") return;
      if (!enabled) return;
      if (isFresh) return;
      const coords = await getCurrentCoords();
      if (!coords) return;
      await writeCoords(coords.lat, coords.lng);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [hasIdentity, permission, enabled, isFresh, writeCoords]);

  const handleTap = async () => {
    if (busy || !hasIdentity) return;
    setBusy(true);
    try {
      if (isOn) {
        // Visual-only OFF (D2) — clear preference, keep coords.
        await setEnabled(false);
        return;
      }
      // OFF → ON: request permission + coords + persist.
      const coords = await getCurrentCoords();
      // Refresh cached permission state after the prompt.
      const p = await checkLocationPermission();
      setPermission(p);
      if (!coords) return; // getCurrentCoords toasted the reason.
      await writeCoords(coords.lat, coords.lng);
    } finally {
      setBusy(false);
    }
  };

  const denied = permission === "denied" && !isOn;

  const caption = loading
    ? "Checking your location…"
    : busy
      ? "Getting your location…"
      : isOn
        ? updatedAt
          ? "On — using your location"
          : "On"
        : denied
          ? "Off — location denied. Enable in Settings."
          : "Off — tap to enable location";

  return (
    <div
      style={{
        margin: "12px 16px",
        padding: 16,
        borderRadius: 16,
        background: "#FFFFFF",
        border: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
      data-is-fresh={isFresh ? "true" : "false"}
      data-enabled={enabled ? "true" : "false"}
      data-permission={permission}
    >
      <span
        aria-hidden
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: isOn ? CORAL : "#EFEBE3",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: isOn ? "#FFFFFF" : MUTED,
        }}
      >
        <MapPin size={18} strokeWidth={2.25} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>
          Shop nearby stores
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
          {caption}
        </div>
        {denied && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void openAppSettings();
            }}
            style={{
              marginTop: 6,
              fontSize: 12,
              fontWeight: 600,
              color: CORAL,
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            Open Settings
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={handleTap}
        disabled={busy || !hasIdentity}
        aria-pressed={isOn}
        aria-label={isOn ? "Turn off shop nearby stores" : "Turn on shop nearby stores"}
        style={{
          width: 44,
          height: 26,
          borderRadius: 999,
          border: "none",
          padding: 3,
          background: isOn ? CORAL : "#D9D4CB",
          cursor: busy ? "wait" : "pointer",
          opacity: busy ? 0.7 : 1,
          position: "relative",
          transition: "background 120ms ease",
        }}
      >
        <span
          style={{
            display: "block",
            width: 20,
            height: 20,
            borderRadius: 999,
            background: "#FFFFFF",
            transform: isOn ? "translateX(18px)" : "translateX(0)",
            transition: "transform 120ms ease",
            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
          }}
        />
      </button>
    </div>
  );
}

export default OfflineShoppingToggle;
